import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { fmtMoney } from "./money";
import { aimeFrom, piaFrom, benefitAt, fraMonths, fraLabel, hasCredits } from "./retire";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Estimates your monthly Social Security retirement benefit from your birth year, earnings and years worked, at any claiming age between 62 and 70 (to the month), using the SSA's 2026 benefit formula and your exact full retirement age.`,
  how: `Enter your birth year, current annual earnings and how many years you have worked (up to 35). Pick the age and month you plan to claim, and compare the early, full-retirement-age and age-70 amounts below. Amounts are in today's dollars, before future cost-of-living increases.`,
  tip: `Claiming before your full retirement age permanently reduces your monthly benefit, while delaying past it increases it (up to age 70). It is one of the most consequential retirement decisions, so compare a few claiming ages and check your real earnings record at ssa.gov/myaccount.`,
};
const SEL = "border rounded px-2 py-2 w-full bg-background text-sm";
const usd = (n: number) => fmtMoney(n, "USD", 2);

export default function SocialSecurityCalculator() {
  const [birthYear, setBirthYear] = useState("1985");
  const [earnings, setEarnings] = useState("80000");
  const [years, setYears] = useState("35");
  const [claimY, setClaimY] = useState("67");
  const [claimM, setClaimM] = useState("0");

  const by = parseNumber(birthYear), e = parseNumber(earnings), yw = parseNumber(years);
  const thisYear = new Date().getFullYear();
  const error =
    by == null || by < 1920 || by > thisYear - 18 ? `Enter a birth year between 1920 and ${thisYear - 18}.`
    : e == null || e < 0 ? "Enter your annual earnings (0 or more)."
    : yw == null || yw < 0 || yw > 60 ? "Enter the years you have worked (0 to 35+)." : "";
  const ok = !error;
  const noCredits = ok && !hasCredits(yw!);
  const pia = ok ? piaFrom(aimeFrom(e!, yw!)) : 0;
  const fra = ok ? fraMonths(by!) : 0;
  const currentAge = ok ? thisYear - by! : 0;
  const claimMonths = Math.min(70 * 12, Math.max(62 * 12, (Number(claimY) || 67) * 12 + (Number(claimM) || 0)));
  const options = ok ? [{ label: "Earliest (62)", m: 62 * 12 }, { label: `Full retirement age (${fraLabel(by!)})`, m: fra }, { label: "Latest (70)", m: 70 * 12 }].filter((o) => o.m >= Math.min(currentAge * 12, 70 * 12) || o.m === fra) : [];
  const { blocked } = useLogToolUsage(ok && !noCredits);
  const pct = (b: number) => (pia ? Math.round((b / pia) * 1000) / 10 : 0);

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Birth year</Label><Input inputMode="numeric" aria-label="Birth year" value={birthYear} onChange={(ev) => setBirthYear(ev.target.value)} /></div>
          <div><Label className="text-xs">Annual earnings ($)</Label><Input inputMode="decimal" aria-label="Annual earnings" value={earnings} onChange={(ev) => setEarnings(ev.target.value)} /></div>
          <div><Label className="text-xs">Years worked (of 35)</Label><Input inputMode="numeric" aria-label="Years worked" value={years} onChange={(ev) => setYears(ev.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Claim at age</Label>
            <select className={SEL} aria-label="Claim age years" value={claimY} onChange={(ev) => setClaimY(ev.target.value)}>{[62, 63, 64, 65, 66, 67, 68, 69, 70].map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
          <div><Label className="text-xs">and months</Label>
            <select className={SEL} aria-label="Claim age months" value={claimM} onChange={(ev) => setClaimM(ev.target.value)} disabled={claimY === "70"}>{Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{i}</option>)}</select></div>
        </div>
        {error && <p className="text-sm text-red-600" data-testid="ss-error">{error}</p>}
        {noCredits && <NoticeBox className="text-sm" testId="ss-credits">Retirement benefits need 40 work credits, which is about 10 years of work. With fewer than 10 years you would not qualify on your own record.</NoticeBox>}
        {ok && !noCredits && (blocked ? <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p> : (
          <>
            <div className="p-4 rounded-xl border bg-primary/5 text-center">
              <p className="text-xs text-muted-foreground">Full Retirement Age (FRA)</p>
              <p className="text-xl font-bold text-primary" data-testid="ss-fra">{fraLabel(by!)}</p>
              <p className="text-xs text-muted-foreground mt-1">Full benefit (PIA): {usd(pia)}/mo</p>
            </div>
            <div className="p-4 rounded-xl border bg-card text-center">
              <p className="text-xs text-muted-foreground">Claiming at {Math.floor(claimMonths / 12)}{claimMonths % 12 ? ` and ${claimMonths % 12} months` : ""}</p>
              <p className="text-2xl font-bold" data-testid="ss-chosen">{usd(benefitAt(pia, by!, claimMonths))}/mo</p>
              <p className="text-xs text-muted-foreground mt-1">{pct(benefitAt(pia, by!, claimMonths))}% of your full benefit · {usd(benefitAt(pia, by!, claimMonths) * 12)} a year</p>
            </div>
            <div className="space-y-2">
              {options.map((o) => (
                <div key={o.label} className={`flex items-center justify-between p-3 rounded-lg border ${o.m === fra ? "bg-primary/5 border-primary/20" : "bg-card"}`}>
                  <p className="text-sm font-medium">{o.label}</p>
                  <p className="text-base font-bold" data-testid={o.m === 62 * 12 ? "ss-62" : o.m === 70 * 12 ? "ss-70" : "ss-fra-amount"}>{usd(benefitAt(pia, by!, o.m))}/mo</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-center">A spouse with a smaller record can receive up to 50% of your full benefit ({usd(pia / 2)}/mo) at their own full retirement age.</p>
          </>
        ))}
        <p className="text-xs text-muted-foreground text-center">
          Simplified estimate using the SSA's 2026 bend points (90/32/15%) with your current earnings treated as your career average, scaled by years worked out of 35. Shown in today's dollars, before cost-of-living increases. Your real benefit uses your indexed 35-year record: check ssa.gov/myaccount or the SSA Retirement Estimator.
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
